import {
  AcceptCompanyUser,
  CreateCompanyForm,
  CreateCompanyUserBody,
  UpdateCompanyForm,
  UpdateCompanyUserBody
} from '@shared/dto/companies.dto'
import { Company, CompanyUsersStatus } from '@shared/types/companies.types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../useAuthStore'
import axiosClient from '../useAxios'

export const useCreateCompanyMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAuthStore()
  return useMutation({
    mutationKey: ['companies', 'create'],
    mutationFn: async (data: CreateCompanyForm): Promise<Company> => {
      const response = await axiosClient.post('/companies', data)
      return response.data.data.company
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['users', userId, 'companies']
      })
    }
  })
}

export const useDeleteCompanyMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAuthStore()
  return useMutation({
    mutationKey: ['companies', 'delete'],
    mutationFn: async (companyId: string): Promise<void> => {
      await axiosClient.delete(`/companies/${companyId}`)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['users', userId, 'companies', CompanyUsersStatus.ACTIVE]
      })
    }
  })
}

export const useAbandonCompanyMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAuthStore()
  return useMutation({
    mutationKey: ['companies', 'abandon'],
    mutationFn: async ({
      userId,
      companyId
    }: {
      userId: string
      companyId: string
    }): Promise<void> => {
      await axiosClient.delete(`/users/${userId}/companies/${companyId}`)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['users', userId, 'companies']
      })
    }
  })
}

export const useExpelUserCompanyMutation = (companyId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['companies', 'user', 'expel'],
    mutationFn: async ({ userId }: { userId: string }): Promise<void> => {
      await axiosClient.delete(`/companies/${companyId}/users/${userId}`)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          'companies',
          companyId,
          'users',
          {
            status: CompanyUsersStatus.ACTIVE
          }
        ]
      })
    }
  })
}

export const useUpdateCompanyMutation = (companyId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['companies', 'update'],
    mutationFn: async (data: UpdateCompanyForm): Promise<Company> => {
      const response = await axiosClient.patch(`/companies/${companyId}`, data)
      return response.data.data.company
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['companies', companyId]
      })
    }
  })
}

export const useUpdateCompanyUserMutation = (
  companyId: string,
  workspaceId?: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['companies', 'update-user'],
    mutationFn: async ({
      userId,
      data
    }: {
      userId: string
      data: UpdateCompanyUserBody
    }): Promise<void> => {
      await axiosClient.patch(`/companies/${companyId}/users/${userId}`, data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          'companies',
          companyId,
          'users',
          {
            status: CompanyUsersStatus.ACTIVE
          }
        ]
      })

      if (workspaceId) {
        await queryClient.invalidateQueries({
          queryKey: [
            'companies',
            companyId,
            'workspaces',
            workspaceId,
            'users',
            {
              status: CompanyUsersStatus.ACTIVE
            }
          ]
        })
      }
    }
  })
}

export const useCreateCompanyUserMutation = (companyId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['companies', 'create-user'],
    mutationFn: async (data: CreateCompanyUserBody): Promise<void> => {
      await axiosClient.post(`/companies/${companyId}/users`, data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          'companies',
          companyId,
          'users',
          {
            status: CompanyUsersStatus.PENDING_USER
          }
        ]
      })
    }
  })
}

export const useResendInvitationMutation = (companyId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['companies', 'resend-invitation'],
    mutationFn: async ({
      companyId,
      userId
    }: {
      companyId: string
      userId: string
    }): Promise<void> => {
      await axiosClient.post(`/companies/${companyId}/users/${userId}/resend`)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          'companies',
          companyId,
          'users',
          {
            status: CompanyUsersStatus.PENDING_COMPANY
          }
        ]
      })
      await queryClient.invalidateQueries({
        queryKey: [
          'companies',
          companyId,
          'users',
          {
            status: CompanyUsersStatus.PENDING_USER
          }
        ]
      })
    }
  })
}

export const useRejectInvitationMutation = (companyId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['companies', 'reject-invitation'],
    mutationFn: async ({
      companyId,
      userId
    }: {
      companyId: string
      userId: string
    }): Promise<void> => {
      await axiosClient.post(`/companies/${companyId}/users/${userId}/reject`)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          'companies',
          companyId,
          'users',
          {
            status: CompanyUsersStatus.PENDING_USER
          }
        ]
      })
    }
  })
}

export const useAcceptCompanyUserMutation = (
  companyId: string,
  userId: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['companies', 'accept-user'],
    mutationFn: async (data: AcceptCompanyUser): Promise<void> => {
      await axiosClient.post(
        `/companies/${companyId}/users/${userId}/accept`,
        data
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          'companies',
          companyId,
          'users',
          {
            status: CompanyUsersStatus.ACTIVE
          }
        ]
      })
    }
  })
}
